import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { formatBytes } from '@pirouette/shared';
import {
  useSetByoBucket,
  useSetVideoStorageMode,
  useVerifyVideoBucket,
  useVideoStorageSettings,
  type ByoProvider,
  type SetByoBucketInput,
  type VideoStorageMode,
} from '@/data/videoStorage';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/storage')({
  component: AdminStorage,
});

const byoSchema = z.object({
  provider: z.enum(['cloudflare_r2', 'backblaze_b2', 'wasabi', 'aws_s3']),
  endpoint: z
    .string()
    .url()
    .refine((value) => value.startsWith('https://'), {
      message: 'Endpoint must use https.',
    }),
  region: z.string().trim().min(1, 'Region is required.'),
  bucket: z.string().trim().min(1, 'Bucket is required.'),
  prefix: z.string().trim(),
  accessKeyId: z.string().trim().min(1, 'Access key ID is required.'),
  secret: z.string().min(1, 'Secret key is required.'),
});

const providerLabels: Record<ByoProvider, string> = {
  cloudflare_r2: 'Cloudflare R2',
  backblaze_b2: 'Backblaze B2',
  wasabi: 'Wasabi',
  aws_s3: 'AWS S3',
};

function AdminStorage() {
  const settings = useVideoStorageSettings();
  const setMode = useSetVideoStorageMode();
  const setByo = useSetByoBucket();
  const verify = useVerifyVideoBucket();
  const [form, setForm] = useState<SetByoBucketInput>({
    provider: 'backblaze_b2',
    endpoint: '',
    region: '',
    bucket: '',
    prefix: '',
    accessKeyId: '',
    secret: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!settings.data) return;
    setForm((current) => ({
      ...current,
      provider: settings.data.byoProvider ?? current.provider,
      endpoint: settings.data.byoEndpoint ?? current.endpoint,
      region: settings.data.byoRegion ?? current.region,
      bucket: settings.data.byoBucket ?? current.bucket,
      prefix: settings.data.byoPrefix ?? current.prefix,
      accessKeyId: settings.data.byoAccessKeyId ?? current.accessKeyId,
      secret: '',
    }));
  }, [settings.data]);

  async function handleMode(mode: VideoStorageMode) {
    await setMode.mutateAsync(mode);
    toast({ title: 'Video storage mode updated', variant: 'success' });
  }

  function parseByoForm(): SetByoBucketInput | null {
    const parsed = byoSchema.safeParse(form);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Check the bucket settings.');
      return null;
    }
    setFormError(null);
    return parsed.data;
  }

  async function handleSaveBucket() {
    const parsed = parseByoForm();
    if (!parsed) return;
    await setByo.mutateAsync(parsed);
    toast({ title: 'Bucket settings saved', variant: 'success' });
  }

  async function handleTestBucket() {
    const parsed = parseByoForm();
    if (!parsed) return;
    await setByo.mutateAsync(parsed);
    await verify.mutateAsync();
    setForm((current) => ({ ...current, secret: '' }));
    toast({ title: 'Bucket verified', variant: 'success' });
  }

  if (settings.isLoading) return <p aria-live="polite">Loading...</p>;

  if (!settings.data) {
    return (
      <Empty
        title="Video storage settings unavailable"
        description="Refresh and try again, or check the video storage migration."
      />
    );
  }

  const usagePct =
    settings.data.quotaGb > 0
      ? Math.min(
          100,
          Math.round((settings.data.usageBytes / (settings.data.quotaGb * 1024 ** 3)) * 100),
        )
      : 0;

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Settings
        </p>
        <h1 className="mt-2">Video storage</h1>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge tone={settings.data.videoEnabled ? 'success' : 'warn'}>
            {settings.data.videoEnabled ? 'Enabled' : 'Entitlement off'}
          </Badge>
          <Badge tone="neutral">
            {settings.data.mode === 'byo_bucket' ? 'BYO bucket' : 'Hosted'}
          </Badge>
        </div>
      </header>

      <section aria-labelledby="mode-heading" className="grid gap-3 sm:grid-cols-2">
        <h2 id="mode-heading" className="sr-only">
          Storage mode
        </h2>
        <label className="surface-elevated cursor-pointer p-5">
          <input
            type="radio"
            name="video-mode"
            value="pirouette_hosted"
            checked={settings.data.mode === 'pirouette_hosted'}
            onChange={() => void handleMode('pirouette_hosted')}
            className="mr-2"
          />
          <span className="font-medium">Pirouette-hosted</span>
          <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Platform Cloudflare R2 storage with a managed quota.
          </p>
        </label>
        <label className="surface-elevated cursor-pointer p-5">
          <input
            type="radio"
            name="video-mode"
            value="byo_bucket"
            checked={settings.data.mode === 'byo_bucket'}
            onChange={() => void handleMode('byo_bucket')}
            className="mr-2"
          />
          <span className="font-medium">Bring your own bucket</span>
          <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Connect an S3-compatible bucket your studio owns.
          </p>
        </label>
      </section>

      <section aria-labelledby="usage-heading" className="surface-elevated p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="usage-heading" className="font-medium text-[var(--text-lg)]">
            Usage
          </h2>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {formatBytes(settings.data.usageBytes)} of {settings.data.quotaGb} GB
          </p>
        </div>
        <div className="mt-4 h-3 overflow-hidden rounded-[var(--radius-sm)] bg-[var(--color-surface-sunken)]">
          <div
            className="h-full bg-[var(--color-accent)]"
            style={{ width: `${usagePct}%` }}
            aria-hidden="true"
          />
        </div>
      </section>

      <section aria-labelledby="byo-heading" className="surface-elevated p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="byo-heading" className="font-medium text-[var(--text-lg)]">
            BYO bucket
          </h2>
          <Badge tone={settings.data.byoVerifiedAt ? 'success' : 'neutral'}>
            {settings.data.byoVerifiedAt ? 'Verified' : 'Not verified'}
          </Badge>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="byo-provider">Provider</Label>
            <select
              id="byo-provider"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={form.provider}
              onChange={(e) => setForm({ ...form, provider: e.target.value as ByoProvider })}
            >
              {Object.entries(providerLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <Field
            id="byo-endpoint"
            label="Endpoint"
            value={form.endpoint}
            placeholder="https://s3.us-west-002.backblazeb2.com"
            onChange={(endpoint) => setForm({ ...form, endpoint })}
          />
          <Field
            id="byo-region"
            label="Region"
            value={form.region}
            placeholder="us-west-002"
            onChange={(region) => setForm({ ...form, region })}
          />
          <Field
            id="byo-bucket"
            label="Bucket"
            value={form.bucket}
            onChange={(bucket) => setForm({ ...form, bucket })}
          />
          <Field
            id="byo-prefix"
            label="Prefix"
            value={form.prefix}
            placeholder="videos"
            onChange={(prefix) => setForm({ ...form, prefix })}
          />
          <Field
            id="byo-access-key"
            label="Access key ID"
            value={form.accessKeyId}
            onChange={(accessKeyId) => setForm({ ...form, accessKeyId })}
          />
          <Field
            id="byo-secret"
            label={settings.data.hasSecret ? 'Secret key (saved, enter to replace)' : 'Secret key'}
            type="password"
            value={form.secret}
            onChange={(secret) => setForm({ ...form, secret })}
          />
        </div>

        {formError && (
          <p role="alert" className="mt-4 text-[var(--color-danger)] text-[var(--text-sm)]">
            {formError}
          </p>
        )}

        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            variant="secondary"
            onClick={() => void handleSaveBucket()}
            disabled={setByo.isPending}
          >
            Save bucket
          </Button>
          <Button
            onClick={() => void handleTestBucket()}
            disabled={setByo.isPending || verify.isPending}
          >
            {verify.isPending ? 'Testing...' : 'Test connection'}
          </Button>
        </div>

        <div className="mt-6 grid gap-3 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          <p>
            CORS must allow this app origin for PUT, GET, HEAD, and OPTIONS with the Content-Type
            header.
          </p>
          <p>
            Use a restricted access key for this bucket only. The secret is encrypted and never
            displayed back after saving.
          </p>
        </div>
      </section>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
